import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateUser, usersQueryKeys, type UpdateUserData, type User } from '@api/users';
import { ApiError } from '@api/client';
import { toast } from '@/hooks/use-toast';
import { toastWithLink } from '@/components/ui/toast';
import { useTranslation } from '../useTranslation';

type Translate = (key: string, options?: Record<string, string>) => string;

function updateSuccessDescription(
  t: Translate,
  sent: UpdateUserData,
  saved: User,
  previous: User | undefined,
): { label: string; wrapLabel?: boolean } {
  if (sent.status) {
    if (sent.status === 'verified') return { label: t('update.activated') };
    if (sent.status === 'closed') return { label: t('update.deactivated') };
    if (previous && saved.status !== previous.status) {
      return {
        label: t('update.status_changed', {
          from: t(`tabs.${previous.status}`),
          to: t(`tabs.${saved.status}`),
        }),
        wrapLabel: true,
      };
    }
  }

  if (previous && saved.role !== previous.role) {
    return {
      label: t('update.role_changed', {
        from: t(`roles.${previous.role}`),
        to: t(`roles.${saved.role}`),
      }),
      wrapLabel: true,
    };
  }

  return { label: t('update.success') };
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: updateUser,
    onMutate: (variables) => ({
      previous: queryClient.getQueryData<User>(usersQueryKeys.detail(variables.id)),
    }),
    onSuccess: (response, variables, context) => {
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.lists() });
      queryClient.setQueryData(usersQueryKeys.detail(variables.id), response.user);

      toastWithLink({
        name: response.user.name || response.user.email,
        href: `/users/${response.user.id}/edit`,
        ...updateSuccessDescription(t, variables, response.user, context?.previous),
      });
    },
    onError: (error) => {
      // Field-level 422s render inline on the form.
      if (error instanceof ApiError && error.fieldErrors) return;
      toast({
        description: error.message || t('update.error_message'),
        variant: 'destructive',
      });
    },
  });
}
